import { getAuth, signInWithPopup, GoogleAuthProvider as FirebaseGoogleAuthProvider, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { AuthProvider, AuthUser, SocialAuthConfig } from './types';

export class GoogleAuthService implements AuthProvider {
  private auth = getAuth();

  private provider: FirebaseGoogleAuthProvider;
  
  private config: SocialAuthConfig;

  constructor(config: SocialAuthConfig) {
    this.config = config;
    this.provider = new FirebaseGoogleAuthProvider();
    
    // Add custom scopes if provided
    if (config.scope) {
      config.scope.forEach(scope => this.provider.addScope(scope));
    }
    
    // Set custom parameters
    this.provider.setCustomParameters({
      'display': 'popup'
    });
  }

  async signIn(): Promise<AuthUser | null> {
    try {
      const result = await signInWithPopup(this.auth, this.provider);
      const token = await result.user.getIdToken();
      return this.mapFirebaseUser(result.user, token, 'google');
    } catch (error) {
      console.error('Google sign-in failed:', error);
      throw new Error('Google sign-in failed');
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      console.error('Google sign-out failed:', error);
      throw new Error('Google sign-out failed');
    }
  }

  getCurrentUser(): AuthUser | null {
    const user = this.auth.currentUser;
    if (!user) return null;
    
    return this.mapFirebaseUser(user, '', 'google');
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, (firebaseUser) => {
      if (firebaseUser) {
        const user = this.mapFirebaseUser(firebaseUser, '', 'google');
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  private mapFirebaseUser(firebaseUser: any, token: string, provider: string): AuthUser {
    const providerData = firebaseUser.providerData?.find((p: any) => p.providerId === 'google.com');
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      accessToken: token || firebaseUser.accessToken || '',
      provider,
      providerData: providerData ? {
        providerId: providerData.providerId,
        uid: providerData.uid,
        displayName: providerData.displayName,
        email: providerData.email,
        photoURL: providerData.photoURL
      } : undefined
    };
  }
} 